import { Component, DestroyRef, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReviewerService, ReviewDetailsFilters } from '../core/services/reviewer.service';
import { UserRoleService } from '../core/services/user-role.service';
import { DateFormatService } from '../core/services/date-format.service';
import { RebarAuthService } from '../core/rebarauth/rebar.auth.service';
import { ExcelExportService } from '../core/services/excel-export.service';
interface ColumnItem {
  key: string;
  label: string;
  visible: boolean;
}

interface ColumnGroup {
  name: string;
  expanded: boolean;
  columns: ColumnItem[];
}

interface ColumnFilter {
  op1: string;
  val1: string;
}

@Component({
  standalone: true,
  selector: 'app-review-management',
  imports: [MatIconModule, CommonModule, FormsModule],
  templateUrl: './review-management.component.html',
  styleUrl: './review-management.component.css',
})

export class ReviewManagementComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private dateFormatSvc = inject(DateFormatService);
  private excelExportSvc = inject(ExcelExportService);
  readonly pageTitle = 'Review Management';
  isExporting = false;
  pageSize = 10;
  currentPage = 1;
  goToPageInput: number = 1;
  searchText = '';
  sortColumn = 'id';
  sortDirection: 'asc' | 'desc' = 'desc';
  isFilterOpen = false;
  isLoading = false;
  successToastMessage: string | null = null;
  isDeleteSuccess = false;

  showDeleteModal = false;
  isDeletingReview = false;
  private pendingDeleteItem: any = null;
  userName: string;
  hasDashboardFilter = false;
  
  // Column filter state
  activeFilterColumn: string | null = null;
  operators = ['Contains', 'Not Contains', 'Equals', 'Starts With', 'Ends With'];
  filterableColumns = [
  'id',
  'name',
  'employee',
  'status',
  'year',
  'client',
  'project',
  'contract',
  'additionalContract',
  'market',
  'marketUnit',
  'customerRelationship',
  'planName',
  'qadRecommendation',
  'clientQuality',
  'clientAccLead',
  'operationsAccLead',
  'masterClientID',
  'deliveryCountry',
  'locationsToBeReviewed',
  'isPrimaryLocation',
  'quarter',
  'proposedMonth',
  'comments',
  'attachment',
  'notes/instructions',
  'createdBy',
  'createdDate',
  'rcManager',
  'rcSupervisor',
  'rcReviewer',
  'secondaryManager',
  'secondarySupervisor',
  'secondaryReviewer',
  'scheduleID',
  'accentureOperationsSL',
  'parentChild',
  'parentReviewID',
  'targetReportPublication',
  'fieldWorkStartDate',
  'fieldWorkEndDate',
  'singleOrMultiLocation'
  ];
  filters: { [key: string]: ColumnFilter } = {};

  // Column selector state
  basicExpanded = true;
  scheduleExpanded = true;
  columnSearchText = '';
  data: any[] = [];
  private defaultOrder: any[] = [];
  private defaultVisibleKeys!: Set<string>;

  reviewBasicColumns: ColumnItem[] = [
    { key: 'id', label: 'Review ID', visible: true },
    { key: 'name', label: 'Review Name', visible: true },
    { key: 'employee', label: 'Employee Name', visible: true },
    { key: 'status', label: 'Current Status', visible: true },
    { key: 'year', label: 'Proposed Year', visible: true },
    { key: 'client', label: 'Master Client Name', visible: true },
    { key: 'project', label: 'D&T Project Name', visible: true },
    { key: 'contract', label: 'Contract Number', visible: true },
    { key: 'additionalContract', label: 'Additional Contract Number', visible: false },
    { key: 'market', label: 'Market', visible: false },
    { key: 'marketUnit', label: 'Market Unit', visible: false },
    { key: 'customerRelationship', label: 'Customer Relationship Name', visible: false },
    { key: 'planName', label: 'Plan Name', visible: false },
    { key: 'qadRecommendation', label: 'QAD Recommendation (Opp)/QAD Overall Status(Del)', visible: false },
    { key: 'clientQuality', label: 'Client Quality (CQI)', visible: false },
    { key: 'clientAccLead', label: 'Client Account Lead (GR)', visible: false },
    { key: 'operationsAccLead', label: "Operation's Account Lead - Overall", visible: false },
    { key: 'masterClientID', label: 'Master Client ID', visible: false },
    { key: 'deliveryCountry', label: 'Delivery Country*', visible: false },
    { key: 'locationsToBeReviewed', label: 'Locations To Be reviewed*', visible: false },
    { key: 'isPrimaryLocation', label: 'Is it Primary Location (TBD)?', visible: false },
    { key: 'quarter', label: 'Quarter', visible: false },
    { key: 'proposedMonth', label: 'Proposed Month', visible: false },
    { key: 'comments', label: 'Comments', visible: false },
    { key: 'attachment', label: 'Attachment', visible: false },
    { key: 'notes/instructions', label: 'Notes/Instructions', visible: false },
    { key: 'createdBy', label: 'Created By', visible: false },
    { key: 'createdDate', label: 'Created Date', visible: false },
    { key: 'rcManager', label: 'R&C Manager*', visible: false },
    { key: 'rcSupervisor', label: 'R&C Supervisor', visible: false },
    { key: 'rcReviewer', label: 'R&C Reviewer', visible: false },
    { key: 'secondaryManager', label: 'Secondary Manager', visible: false },
    { key: 'secondarySupervisor', label: 'Secondary Supervisor', visible: false },
    { key: 'secondaryReviewer', label: 'Secondary Reviewer', visible: false }
  ];

  scheduleColumns: ColumnItem[] = [
    { key: 'scheduleID', label: 'Schedule ID', visible: false },
    { key: 'accentureOperationsSL', label: 'Accenture Operation Service Line', visible: false },
    { key: 'parentChild', label: 'Parent/Child (TBD):', visible: false },
    { key: 'parentReviewID', label: 'Parent Review ID (TBD):', visible: false },
    { key: 'targetReportPublication', label: 'Target for report publications', visible: false },
    { key: 'fieldWorkStartDate', label: 'Field Work Start Date', visible: false },
    { key: 'fieldWorkEndDate', label: 'Field Work End Date', visible: false },
    { key: 'singleOrMultiLocation', label: 'Single or Multiple Location Review? (TBD)', visible: false }
  ];

  get isReviewer(): boolean {
    return this.userRoleService.isReviewer;
  }

  get isAdmin(): boolean {
    return this.userRoleService.isAdmin;
  }

  get isRandCMember(): boolean {
    return this.userRoleService.isRCMember;
  }

  constructor(
    private reviewerService: ReviewerService,
    private router: Router,
    private route: ActivatedRoute,
    private userRoleService: UserRoleService,
    private rebarAuth: RebarAuthService,
  ) {
    this.filterableColumns.forEach(c => {
      this.filters[c] = { op1: 'Contains', val1: '' };
    });
    this.defaultVisibleKeys = new Set([
      ...this.reviewBasicColumns.filter(c => c.visible).map(c => c.key),
      ...this.scheduleColumns.filter(c => c.visible).map(c => c.key),
    ]);
  }

  selectAll(): void {
    [...this.reviewBasicColumns, ...this.scheduleColumns].forEach(c => c.visible = true);
  }

  clearAll(): void {
    [...this.reviewBasicColumns, ...this.scheduleColumns].forEach(c => {
      c.visible = this.defaultVisibleKeys.has(c.key);
    });
  }

  closeSuccessToast() {
    this.successToastMessage = null;
    this.isDeleteSuccess = false;
    document.body.style.overflow = '';
  }

  openReview(item: any) {
    const reviewId = item?.id;
    //if (reviewId == null || this.isRandCMember) return;
    this.router.navigate(['/review-management/review-process'], {
      queryParams: { reviewId, entryMode: 'action' },
      state: { reviewId, entryMode: 'action' }
    });
  }

  openView(item: any) {
    const reviewId = item?.id;
    if (reviewId == null) return;
    this.router.navigate(['/review-management/review-process'], {
      queryParams: { reviewId, entryMode: 'view' },
      state: { reviewId, entryMode: 'view' }
    });
  }

  openEdit(item: any) {
    const reviewId = item?.id;
    // if (reviewId == null || this.isRandCMember) return;
    this.router.navigate(['/review-management/review-process'], {
      queryParams: { reviewId, entryMode: 'edit' },
      state: { reviewId, entryMode: 'edit' }
    });
  }

  ngOnInit() {
    this.userName = this.rebarAuth.getUserName();
    const navState = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    const reviewTypeName: string = navState?.['reviewTypeName'] ?? 'Standard Review';
    if (navState?.['draftSuccess']) {
      this.successToastMessage = `${reviewTypeName} was saved successfully in draft`;
    } else if (navState?.['submitSuccess']) {
      this.successToastMessage = `${reviewTypeName} was submitted successfully`;
    }
    if (this.successToastMessage) {
      setTimeout(() => (this.successToastMessage = null), 5000);
    }
    let rawDashboardFilters = navState?.['reviewDetailsFilters'];
    if (!rawDashboardFilters) {
      const filterToken = this.route.snapshot.queryParamMap.get('dashboardFilterToken');
      const storageKey = filterToken
        ? `pending_reviewDetailsFilters:${filterToken}`
        : 'pending_reviewDetailsFilters';
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try { rawDashboardFilters = JSON.parse(stored); } catch { }
        localStorage.removeItem(storageKey);
      } else if (filterToken) {
        const legacyStored = localStorage.getItem('pending_reviewDetailsFilters');
        if (legacyStored) {
          try { rawDashboardFilters = JSON.parse(legacyStored); } catch { }
          localStorage.removeItem('pending_reviewDetailsFilters');
        }
      }
    }
    const initialFilters = this.getInitialReviewFilters(rawDashboardFilters);
    this.hasDashboardFilter = !!rawDashboardFilters && Object.values(rawDashboardFilters).some(v => v !== null && v !== undefined && v !== '');
    // Clear history state so the toast doesn't re-appear on page reload
    history.replaceState({}, '', window.location.href);

    this.isLoading = true;
    this.reviewerService.getReviewDetails(initialFilters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (res: any[]) => {
    this.data = res.map(item => ({
      id: item.ReviewID,
      name: item.ReviewName,
      employee: item.R_ClientAccountLead,
      status: item.CurrentStatus,
      year: item.R_ProposedYearDisplayName,
      client: item.R_ClientName,
      project: item.R_ProjectName,
      contract: item.R_ContractNumber,
      additionalContract: item.R_AdditionalContractNumber,
      market: item.R_Market,
      marketUnit: item.R_MarketUnit,
      customerRelationship: item.R_CustomerRelationshipName,
      planName: item.R_PlanName,
      qadRecommendation: item.R_QADRecommendation,
      clientQuality: item.R_ClientQualityCQI,
      clientAccLead: item.R_ClientAccountLead,
      operationsAccLead: item.R_OperationAccountLead,
      masterClientID: item.R_MasterClientID,
      deliveryCountry: item.R_DeliveryCountry,
      locationsToBeReviewed: item.R_LocationToBeReviewed,
      isPrimaryLocation: item.R_IsParentLocation === '1' ? 'Yes' : item.R_IsParentLocation === '0' ? 'No' : null,
      quarter: item.R_Quarter,
      proposedMonth: item.R_ProposedMonth,
      comments: item.R_Comments,
      attachment: item.R_UploadFile,
      'notes/instructions': '',
      createdBy: item.CreatedBy,
      createdDate: item.CreatedDate,
      rcManager: item.R_RCManager,
      rcSupervisor: item.R_RCSupervisor,
      rcReviewer: item.R_RCReviewer,
      secondaryManager: item.R_SecondaryRCManager,
      secondarySupervisor: item.R_SecondaryRCSupervisor,
      secondaryReviewer: item.R_SecondaryRCReviewer,
      scheduleID: item.ScheduleID,
      accentureOperationsSL: item.S_AccentureOperationsServiceLine,
      parentChild: item.S_ParentChildType,
      parentReviewID: item.R_ParentReviewID,
      targetReportPublication: this.formatDate(item.S_TargetPublicationDate) ?? this.formatDate(item.S_TargetForReportPublication),
      fieldWorkStartDate: this.formatDate(item.S_FieldworkStartDate),
      fieldWorkEndDate: this.formatDate(item.S_FieldworkEndDate),
      singleOrMultiLocation: item.R_SingleMultipleLocation
    }));
    this.defaultOrder = [...this.data];
    this.data.sort((a: any, b: any) => {
      const va = a['id'];
      const vb = b['id'];
      if (va < vb) return 1;
      if (va > vb) return -1;
      return 0;
    });
    this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        console.error('getReviewDetails error:', err);
      }
    });
  }

  private getInitialReviewFilters(filters?: Partial<ReviewDetailsFilters>): ReviewDetailsFilters {
    return {
      ReviewID: filters?.ReviewID ?? null,
      PropsedYearId: filters?.PropsedYearId ?? null,
      Quarter: filters?.Quarter ?? null,
      ProposedMonth: filters?.ProposedMonth ?? null,
      ClientName: filters?.ClientName ?? null,
      DeliveryCountry: filters?.DeliveryCountry ?? null,
      LocationToBeReviewed: filters?.LocationToBeReviewed ?? null,
      TeamMembers: filters?.TeamMembers ?? null,
      Region: filters?.Region ?? null,
      ReviewTypeID: filters?.ReviewTypeID ?? null,
      ReviewStatus: filters?.ReviewStatus ?? null,
      OverallReportRating: filters?.OverallReportRating ?? null,
    };
  }

  clearDashboardFilter(): void {
    this.hasDashboardFilter = false;
    this.isLoading = true;
    this.reviewerService.getReviewDetails(this.getInitialReviewFilters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.data = res.map(item => ({
            id: item.ReviewID,
            name: item.ReviewName,
            employee: item.R_ClientAccountLead,
            status: item.CurrentStatus,
            year: item.R_ProposedYearDisplayName,
            client: item.R_ClientName,
            project: item.R_ProjectName,
            contract: item.R_ContractNumber,
            additionalContract: item.R_AdditionalContractNumber,
            market: item.R_Market,
            marketUnit: item.R_MarketUnit,
            customerRelationship: item.R_CustomerRelationshipName,
            planName: item.R_PlanName,
            qadRecommendation: item.R_QADRecommendation,
            clientQuality: item.R_ClientQualityCQI,
            clientAccLead: item.R_ClientAccountLead,
            operationsAccLead: item.R_OperationAccountLead,
            masterClientID: item.R_MasterClientID,
            deliveryCountry: item.R_DeliveryCountry,
            locationsToBeReviewed: item.R_LocationToBeReviewed,
            isPrimaryLocation: item.R_IsParentLocation === '1' ? 'Yes' : item.R_IsParentLocation === '0' ? 'No' : null,
            quarter: item.R_Quarter,
            proposedMonth: item.R_ProposedMonth,
            comments: item.R_Comments,
            attachment: item.R_UploadFile,
            'notes/instructions': '',
            createdBy: item.CreatedBy,
            createdDate: item.CreatedDate,
            rcManager: item.R_RCManager,
            rcSupervisor: item.R_RCSupervisor,
            rcReviewer: item.R_RCReviewer,
            secondaryManager: item.R_SecondaryRCManager,
            secondarySupervisor: item.R_SecondaryRCSupervisor,
            secondaryReviewer: item.R_SecondaryRCReviewer,
            scheduleID: item.ScheduleID,
            accentureOperationsSL: item.S_AccentureOperationsServiceLine,
            parentChild: item.S_ParentChildType,
            parentReviewID: item.R_ParentReviewID,
            targetReportPublication: this.formatDate(item.S_TargetForReportPublication),
            fieldWorkStartDate: this.formatDate(item.S_FieldworkStartDate),
            fieldWorkEndDate: this.formatDate(item.S_FieldworkEndDate),
            singleOrMultiLocation: item.R_SingleMultipleLocation,
          }));
          this.defaultOrder = [...this.data];
          this.data.sort((a: any, b: any) => (a['id'] < b['id'] ? 1 : a['id'] > b['id'] ? -1 : 0));
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          console.error('clearDashboardFilter error:', err);
        },
      });
  }

  isUserInRowRoles(item: any): boolean {
    const name = (this.userName || '').toLowerCase();
    if (!name) return false;
    return [
      item.rcManager,
      item.rcSupervisor,
      item.rcReviewer,
      item.secondaryManager,
      item.secondarySupervisor,
      item.secondaryReviewer,
    ].some(role => (role || '').toLowerCase() === name);
  }

  toggleBasicSection() {
    this.basicExpanded = !this.basicExpanded;
  }

  toggleScheduleSection() {
    this.scheduleExpanded = !this.scheduleExpanded;
  }

  /** Returns every data column that may be exported, excluding grid action controls. */
  private exportableColumns(): ColumnItem[] {
    return [...this.reviewBasicColumns, ...this.scheduleColumns];
  }

  isColumnVisible(key: string): boolean {
    const col = this.exportableColumns().find(c => c.key === key);
    return col ? col.visible : true;
  }

  visibleColumnCount(): number {
    return this.exportableColumns().filter(c => c.visible).length;
  }

  totalColumnCount(): number {
    return this.exportableColumns().length;
  }

  private exportData(): any[] {
    if (!this.defaultOrder.length) return this.data;
    const currentRows = new Set(this.data);
    const savedRows = new Set(this.defaultOrder);
    return [
      ...this.defaultOrder.filter(row => currentRows.has(row)),
      ...this.data.filter(row => !savedRows.has(row)),
    ];
  }

  /** Exports all review records and exportable system columns, independent of selector state. */
  async exportAll(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const columns = this.exportableColumns().map(column => ({
        key: column.key,
        header: column.label,
      }));
      const fileName = this.excelExportSvc.buildFileName('ReviewManagement_ExportAll');
      await this.excelExportSvc.exportToExcel(columns, this.data, fileName, 'Review Grid Export All');
    } catch (err) {
      console.error('Export All failed:', err);
    } finally {
      this.isExporting = false;
    }
  }

  /** Exports every review with only the columns enabled in the column selector. */
  async exportVisible(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const columns = this.exportableColumns()
        .filter(column => column.visible)
        .map(column => ({
          key: column.key,
          header: column.label,
        }));
      const fileName = this.excelExportSvc.buildFileName('ReviewManagement_ExportVisible');
      await this.excelExportSvc.exportToExcel(columns, this.data, fileName, 'Review Grid Export Visible');
    } catch (err) {
      console.error('Export Visible failed:', err);
    } finally {
      this.isExporting = false;
    }
  }

  filteredBasicColumns(): ColumnItem[] {
    const q = (this.columnSearchText || '').toLowerCase().trim();
    if (!q) return this.reviewBasicColumns;
    return this.reviewBasicColumns.filter(c => c.label.toLowerCase().includes(q));
  }

  filteredScheduleColumns(): ColumnItem[] {
    const q = (this.columnSearchText || '').toLowerCase().trim();
    if (!q) return this.scheduleColumns;
    return this.scheduleColumns.filter(c => c.label.toLowerCase().includes(q));
  }

  toggleFilterPanel() {
    this.isFilterOpen = !this.isFilterOpen;
  }

  toggleColumnFilter(col: string, event: Event) {
    event.stopPropagation();
    this.activeFilterColumn = this.activeFilterColumn === col ? null : col;
  }

  applyFilter() {
    this.activeFilterColumn = null;
    this.currentPage = 1;
  }

  resetFilter(col: string) {
    this.filters[col] = { op1: 'Contains', val1: '' };
    this.currentPage = 1;
  }

  hasActiveFilter(col: string): boolean {
    const f = this.filters[col];
    return !!(f && f.val1);
  }

  private formatDate(iso: string): string {
    return this.dateFormatSvc.format(iso);
  }

  private matchOperator(cell: string, op: string, val: string): boolean {
    if (!val) return true;
    const c = cell.toLowerCase();
    const v = val.toLowerCase();
    switch (op) {
      case 'Contains':     return c.includes(v);
      case 'Not Contains': return !c.includes(v);
      case 'Equals':       return c === v;
      case 'Starts With':  return c.startsWith(v);
      case 'Ends With':    return c.endsWith(v);
      default:             return true;
    }
  }

  filteredData() {
    const term = this.searchText.trim().toLowerCase();
    return this.data.filter(row => {
      if (String(row.status).toLowerCase() === 'draft'
        && String(row.isPrimaryLocation).toLowerCase() === 'no'
        && String(row.locationsToBeReviewed ?? '').trim() !== '') {
        return false;
      }
      if (term) {
        const visibleKeys = [...this.reviewBasicColumns, ...this.scheduleColumns]
          .filter(c => c.visible)
          .map(c => c.key);
        const matches = visibleKeys.some(col =>
          String((row as any)[col] ?? '').toLowerCase().includes(term)
        );
        if (!matches) return false;
      }
      return this.filterableColumns.every(col => {
        const f = this.filters[col];
        if (!f || !f.val1) return true;
        const cell = String((row as any)[col] ?? '');
        return this.matchOperator(cell, f.op1, f.val1);
      });
    });
  }

  // total pages number
  totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredData().length / this.pageSize));
  }

  // generate pages array
  // totalPagesArray(): number[] {
  //   return Array(this.totalPages()).fill(0).map((x, i) => i + 1);
  // }

  totalPagesArray(): number[] {
  const total = this.totalPages();
  const maxVisible = 10;

  const startPage =
    Math.floor((this.currentPage - 1) / maxVisible) * maxVisible + 1;

  const endPage = Math.min(startPage + maxVisible - 1, total);

  return Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );
}

  goToPage() {
    if (
      this.goToPageInput >= 1 &&
      this.goToPageInput <= this.totalPages()
    ) {
      this.currentPage = this.goToPageInput;
    }
  }

  firstPage() { this.currentPage = 1; this.goToPageInput = 1; }

  prevPage() {
    if (this.currentPage > 1) { this.currentPage--; }
  }

  nextPage() {
    if (this.currentPage < this.totalPages()) { this.currentPage++; }
  }

  lastPage() { this.currentPage = this.totalPages(); this.goToPageInput = this.currentPage; }

  sort(column: string) {
    this.activeFilterColumn = null;
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.data.sort((a: any, b: any) => {
      let valueA = a[column];
      let valueB = b[column];

      // handle strings
      if (typeof valueA === 'string') valueA = valueA.toLowerCase();
      if (typeof valueB === 'string') valueB = valueB.toLowerCase();

      if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    this.currentPage = 1; 
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'unfold_more'; 
    }

    return this.sortDirection === 'asc'
      ? 'arrow_upward'
      : 'arrow_downward';
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: any) {
    const clickedInsideTable = event.target.closest('table');
    if (!clickedInsideTable) {
      this.sortColumn = '';
      this.activeFilterColumn = null;
    }
  }

  navigateToPlanning() {
    this.router.navigate(['/planning']);
  }

  openDeleteModal(item: any) {
    if (!this.isAdmin) return;
    this.pendingDeleteItem = item;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.pendingDeleteItem = null;
  }

  parseAttachmentDocs(attachment: string): { fileName: string; storagePath: string }[] {
    if (!attachment) return [];
    try {
      const parsed = JSON.parse(attachment);
      if (Array.isArray(parsed)) {
        return parsed
          .map((d: any) => ({
            fileName: d.FileName ?? d.fileName ?? '',
            storagePath: d.StorageFilePath ?? d.storagePath ?? '',
          }))
          .filter(d => !!d.fileName);
      }
    } catch { }
    return [{ fileName: attachment, storagePath: '' }];
  }

  downloadAttachment(doc: { fileName: string; storagePath: string }, reviewId: number): void {
    // Blobs are stored under the review folder (StorageFilePath); downloading by the
    // bare file name 404s in blob storage.
    const fileName = doc.storagePath || doc.fileName;
    this.reviewerService.downloadFile(fileName, reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName.split('/').pop() || fileName;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: (err: any) => console.error('Download error:', err?.status, err?.message),
      });
  }

  confirmDelete() {
    if (!this.pendingDeleteItem || this.isDeletingReview) return;
    this.showDeleteModal = false;
    this.isDeletingReview = true;
    const reviewId = this.pendingDeleteItem.id;
    const modifiedBy = this.rebarAuth.getUserName() ?? '';
    this.reviewerService.deleteReview(reviewId, modifiedBy)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.data = this.data.filter((r: any) => r.id !== reviewId);
          this.successToastMessage = 'Review deleted successfully';
          this.isDeleteSuccess = true;
          document.body.style.overflow = 'hidden';
          setTimeout(() => { this.successToastMessage = null; this.isDeleteSuccess = false; document.body.style.overflow = ''; }, 5000);
          this.isDeletingReview = false;
          this.pendingDeleteItem = null;
        },
        error: (err) => {
          console.error('deleteReview error:', err);
          this.isDeletingReview = false;
          this.pendingDeleteItem = null;
        }
      });
  }

}
